import React, { forwardRef, useMemo } from 'react';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { DataGrid } from '../DataGrid/DataGrid';
import { Box } from '@mui/material';
import { DateCell } from '../DataGrid/Cells/DateCell';
import { MultipleBmtCell } from '../DataGrid/Cells/BmtCell';
import { GridColDef } from '@mui/x-data-grid';
import { dateSortValue, formatDateSortValue } from '../../utils/dateUtils';
import { dateSortComparator, withUniqueGridRowIds } from '../../utils/gridUtils';

interface DataGridProps {
  data?: any[];
  loading?: boolean;
  hideBMTColumn?: boolean;
  handleBMTClick?: (bmtItem: any, row: any, summary: any | null) => void;
}

export const AlarmsDataGrid = forwardRef<GridApiPremium | null, DataGridProps>(
  ({ data = [], loading, hideBMTColumn = false, handleBMTClick }, ref) => {
    const alarmDataColumns: GridColDef[] = [
      {
        field: 'deliverySummaries',
        headerName: 'BMT',
        flex: 1,
        minWidth: 120,
        valueGetter: (params) => {
          return params.value.map((summary: any) => summary.bunkerMeterTicketNumber);
        },
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box sx={{ display: 'flex' }}>
              <MultipleBmtCell
                values={params.value || []}
                onBmtClick={(bmt, index) =>
                  handleBMTClick &&
                  handleBMTClick(
                    bmt,
                    params.row,
                    params.row.deliverySummaries ? params.row.deliverySummaries[index] : null,
                  )
                }
              />
            </Box>
          );
        },
      },
      {
        field: 'name',
        headerName: 'Alarm Message',
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'bargeInfo.name',
        headerName: 'Barge name',
        flex: 1,
        minWidth: 120,
        valueGetter: (params: GridRenderCellParams) => params.row?.bargeInfo?.name,
      },
      {
        field: 'deviceInfo.name',
        headerName: 'MFM Device',
        flex: 1,
        minWidth: 170,
        valueGetter: (params: GridRenderCellParams) => params.row?.deviceInfo?.name,
      },
      {
        field: 'startDate',
        headerName: 'Start Date',
        flex: 1,
        minWidth: 120,
        valueGetter: (params) => dateSortValue(params.row.incidentPeriod?.start?.date),
        valueFormatter: ({ value }) => formatDateSortValue(value as number | null),
        sortComparator: dateSortComparator,
        renderCell: (data: GridRenderCellParams) => {
          return <DateCell dateValue={data.row?.incidentPeriod?.start?.date} />;
        },
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        flex: 1,
        minWidth: 120,
        valueGetter: (params) => dateSortValue(params.row.incidentPeriod?.end?.date),
        valueFormatter: ({ value }) => formatDateSortValue(value as number | null),
        sortComparator: dateSortComparator,
        renderCell: (data: GridRenderCellParams) => {
          return <DateCell dateValue={data.row?.incidentPeriod?.end?.date} />;
        },
      },
    ];

    // remove column with BMT number for Alarm logs at Right drawer
    const adjustedColumns = hideBMTColumn ? alarmDataColumns.slice(1) : alarmDataColumns;

    const rows = useMemo(
      () => withUniqueGridRowIds(data, (row) => row.alertId as string | number | undefined),
      [data],
    );

    return (
      <DataGrid
        ref={ref}
        loading={loading}
        rows={rows}
        columns={adjustedColumns}
        getRowId={(row) => row.__gridRowId}
      />
    );
  },
);
